import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, ArrowRight } from 'lucide-react';
import { fmtDate } from '@/lib/utils';
import { CASE_STATUS_LABELS, CASE_STATUS_STYLES } from './caseConstants';

// Compact case history for one customer, shown on their profile. Each row opens
// the case workspace; the footer opens the full queue filtered to them.
export default function RecentCasesList({ customerEmail, limit = 5 }) {
  const email = String(customerEmail || '').toLowerCase();

  const { data: cases = [], isLoading } = useQuery({
    queryKey: ['customer-cases', email],
    queryFn: () => base44.entities.Case.filter({ customer_email: email }, '-last_activity_at', 50),
    enabled: !!email,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <p className="text-white/30 text-xs py-6 border border-white/5 bg-[#0d0d0f] px-5">
        No support cases for this customer yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {cases.slice(0, limit).map((c) => (
        <Link
          key={c.id}
          to={`/admin/crm/cases/${c.id}`}
          className="flex items-center gap-3 border border-white/5 bg-[#0d0d0f] hover:border-primary/40 px-4 py-2.5 transition-colors"
        >
          <span className="text-primary text-[10px] font-bold tracking-widest shrink-0">
            {c.reference_code}
          </span>
          <p className="text-white/80 text-xs truncate flex-1">{c.subject || 'No subject'}</p>
          <span
            className={`text-[9px] font-bold tracking-widest px-2 py-1 border shrink-0 ${
              CASE_STATUS_STYLES[c.status] || 'border-white/10 text-white/40'
            }`}
          >
            {(CASE_STATUS_LABELS[c.status] || c.status || '—').toUpperCase()}
          </span>
          <span className="text-white/30 text-[10px] shrink-0 hidden sm:block">
            {fmtDate(c.last_activity_at || c.created_date)}
          </span>
        </Link>
      ))}

      <Link
        to={`/admin/crm?customer=${encodeURIComponent(email)}`}
        className="inline-flex items-center gap-1.5 text-white/40 hover:text-primary text-[10px] font-bold tracking-widest pt-1 transition-colors"
      >
        VIEW ALL CASES <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}